import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Button, Card, Collapse, Skeleton } from "@mui/material";
import "moment/locale/hu";
import "moment/locale/hu";
import {
  MyHrBackToDate,
  MyHrCurrencyBar,
  MyHrPaycard,
  MyHrSalarySummary,
} from "my-hr/components";
import { useMyHrPayrollData } from "my-hr/hooks";
import { OcCurrency } from "oc/components";
import { useOcClientState } from "oc/hooks";

type MyHrPayrollMonthlyDataProps = {
  year: string;
  month: string;
  contract?: { code: string };
  showComponent?: boolean;
  onBack?: () => void;
  onYearChange?: (year: string) => void;
};

const padToTwo = (num: number): string => {
  return num.toString().padStart(2, "0");
};

export const MyHrPayrollMonthlyData = ({
  year,
  month,
  contract,
  showComponent,
  onBack,
  onYearChange,
}: MyHrPayrollMonthlyDataProps) => {
  const { t, i18n } = useTranslation();
  const [showPaycard, setShowPaycard] = useOcClientState<boolean>(
    "showPaycard",
    false,
  );

  const { data, lastPaycardData, loading } = useMyHrPayrollData({
    year,
    contract: contract?.code ?? "",
  });

  let monthlyData = data?.find((item) => item.month === month);

  let label = moment(`${year}-${month}`).locale(i18n.language).format("MMMM");

  const currentYear = moment().format("YYYY");
  const currentMonth = moment().format("MM");

  if (year === currentYear && month === currentMonth) {
    if (monthlyData?.netWage === 0 && monthlyData?.grossWage === 0) {
      if (lastPaycardData) {
        monthlyData = lastPaycardData;
        label = moment(`${monthlyData?.year}-${monthlyData?.month}`)
          .locale(i18n.language)
          .format("YYYY. MMMM");
      }
    }
  }

  const grossWage = monthlyData?.grossWage || 0;

  const yearToShow = monthlyData?.year || year;
  const monthToShow = monthlyData?.month || year;

  const [selectedPayroll, setSelectedPayroll] = useState(monthToShow);

  const handleNextPaycard = () => {
    let nextMonth = Number(selectedPayroll) + 1;
    let nextYear = Number(yearToShow);

    if (nextMonth > 12) {
      nextMonth = 1; // Reset to January if it exceeds December
      nextYear += 1; // Move to next year
      if (onYearChange) {
        onYearChange(nextYear.toString());
      }
    }

    setSelectedPayroll(padToTwo(nextMonth));
  };

  const handlePreviousPaycard = () => {
    let previousMonth = Number(selectedPayroll) - 1;
    let previousYear = Number(yearToShow);

    if (previousMonth < 1) {
      previousMonth = 12; // Reset to December if it goes below January
      previousYear -= 1; // Move to previous year
      if (onYearChange) {
        onYearChange(previousYear.toString());
      }
    }
    // Ensure the month is always two digits
    setSelectedPayroll(padToTwo(previousMonth));
  };
  const monthChanger = ("0" + selectedPayroll).slice(-2);

  const handleShowPaycard = () => {
    setShowPaycard(true);
    setSelectedPayroll(monthlyData?.month ? monthlyData?.month : monthToShow);
  };

  const handleClosePaycard = () => {
    setShowPaycard(false);
    setSelectedPayroll(lastPaycardData?.month);
  };

  return (
    <React.Fragment>
      <Collapse in={showPaycard} timeout={100} unmountOnExit>
        <MyHrPaycard
          contract={contract?.code ?? ""}
          year={yearToShow}
          month={monthChanger}
          onClose={handleClosePaycard}
          previousPaycard={handlePreviousPaycard}
          nextPaycard={handleNextPaycard}
        />
      </Collapse>
      <Collapse in={!showPaycard} timeout={100} unmountOnExit>
        <MyHrBackToDate
          selectedYear={year}
          showComponent={showComponent}
          // @ts-ignore
          onBack={onBack}
        />
        <Card
          variant="outlined"
          style={{
            borderRadius: "4px",
            borderColor: "white",
            padding: "16px",
          }}
          id="payroll-monthly-card"
        >
          <Box
            style={{
              fontSize: "34px",
              letterSpacing: "0.25px",
              padding: "20px 0px 16px 0px",
              color: "#5E6366",
              borderBottom: "2px solid #C6CACC",
              textAlign: "center",
              textTransform: "capitalize",
            }}
          >
            <Box id="date-label">{label}</Box>
          </Box>
          <Collapse in={grossWage === 0} timeout={100} unmountOnExit>
            {" "}
            <Box padding="30px 16px 14px 16px" style={{ textAlign: "center" }}>
              {loading ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Skeleton width="330px" height="24px" />
                </Box>
              ) : (
                <span>{t("MY_HR_NO_PAYROLL_DATA")}</span>
              )}
            </Box>
          </Collapse>
          <Collapse in={grossWage > 0} timeout={100} unmountOnExit>
            {" "}
            <React.Fragment>
              <Box
                id="currency-data"
                style={{
                  fontSize: "48px",
                  letterSpacing: "0.25px",
                  padding: "16px 0px",
                  color: "#CC0033",
                  textAlign: "center",
                }}
              >
                {OcCurrency({ value: monthlyData?.netWage ?? 0 })}
              </Box>
              <MyHrSalarySummary data={monthlyData} />
              <Box bgcolor="#5E6366" borderRadius="8px" padding="16px 24px">
                <MyHrCurrencyBar
                  label={t("MY_HR_GROSS_WAGE")}
                  value={monthlyData?.grossWage ?? 0}
                  total={monthlyData?.grossWage ?? 0}
                  loading={loading}
                />
                <MyHrCurrencyBar
                  label={t("MY_HR_NET_WAGE")}
                  value={monthlyData?.netWage ?? 0}
                  total={monthlyData?.grossWage ?? 0}
                  loading={loading}
                />
                <MyHrCurrencyBar
                  label={t("MY_HR_ALL_TAX_DEDUCTIONS")}
                  value={monthlyData?.allTaxDeductions ?? 0}
                  total={monthlyData?.grossWage ?? 0}
                  loading={loading}
                />
                <MyHrCurrencyBar
                  label={t("MY_HR_OTHER_DEDUCTIONS")}
                  value={Math.abs(monthlyData?.otherDeductions ?? 0)}
                  total={monthlyData?.grossWage ?? 0}
                  loading={loading}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                marginTop="40px"
                marginBottom="16px"
              >
                <Button
                  id="view-my-paycard"
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={handleShowPaycard}
                >
                  {t("MY_HR_VIEW_MY_PAYCARD")}
                </Button>
              </Box>
            </React.Fragment>
          </Collapse>
        </Card>
      </Collapse>
    </React.Fragment>
  );
};

export default MyHrPayrollMonthlyData;
